import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import {
  LayoutDashboard,
  ClipboardList,
  Repeat,
  MessageCircle,
  MapPin,
  CreditCard,
  FileText,
  Settings,
  Sparkles,
  User,
  LifeBuoy,
  Copy,
  Check,
} from 'lucide-react';
import DashboardLayout, { NavGroup } from './DashboardLayout';
import Button from '@/components/ui/Button';
import { MY_REFERRAL_STATUS } from '@/graphql/operations';
import { clientBottomNav } from '@/lib/navigation';

export default function ClientLayout() {
  const navigate = useNavigate();
  const { t } = useTranslation('client');

  const { data: referralData } = useQuery<{
    myReferralStatus: { code?: string | null };
  }>(MY_REFERRAL_STATUS, { fetchPolicy: 'network-only' });

  const [copied, setCopied] = useState(false);

  const code = referralData?.myReferralStatus?.code;

  const handleCopy = () => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const referralWidget = code ? (
    <div className="mx-3 mb-3 px-3 py-2.5 rounded-xl bg-primary/5 border border-primary/15">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-primary/60 mb-1">
        Cod invitație
      </p>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm font-bold text-gray-800 tracking-widest">
          {code}
        </span>
        <button
          onClick={handleCopy}
          className="text-primary hover:text-primary/70 transition-colors"
          title="Copiază codul"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  ) : (
    <div className="mx-3 mb-3 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
        Cod invitație
      </p>
      <p className="text-xs text-gray-400 leading-snug">
        Disponibil după prima comandă finalizată
      </p>
    </div>
  );

  const navGroups = useMemo<NavGroup[]>(
    () => [
      {
        items: [
          { to: '/cont', icon: LayoutDashboard, label: t('nav.dashboard') },
        ],
      },
      {
        label: t('nav.groupServices'),
        items: [
          { to: '/cont/comenzi', icon: ClipboardList, label: t('nav.bookings') },
          { to: '/cont/abonamente', icon: Repeat, label: t('nav.subscriptions') },
          { to: '/cont/mesaje', icon: MessageCircle, label: t('nav.chat') },
        ],
      },
      {
        label: t('nav.groupFinancial'),
        items: [
          { to: '/cont/plati', icon: CreditCard, label: t('nav.payments') },
          { to: '/cont/facturi', icon: FileText, label: t('nav.invoices') },
        ],
      },
      {
        label: t('nav.groupAccount'),
        items: [
          { to: '/cont/adrese', icon: MapPin, label: t('nav.addresses') },
          { to: '/cont/setari', icon: Settings, label: t('nav.settings') },
          { to: '/cont/ajutor', icon: LifeBuoy, label: t('nav.help') },
        ],
      },
    ],
    [t],
  );

  return (
    <DashboardLayout
      navItems={[]}
      navGroups={navGroups}
      bottomNavItems={clientBottomNav(t)}
      logoIcon={User}
      subtitle={t('nav.subtitle')}
      homeRoute="/cont"
      bottomSlot={referralWidget}
      ctaButton={
        <Button onClick={() => navigate('/rezervare')} className="w-full" size="md">
          <Sparkles className="h-4 w-4" />
          {t('nav.newBooking')}
        </Button>
      }
    />
  );
}
